import { ChannelType } from '@vision/ui/enums';
import dayjs from 'dayjs';
import { toApiCsvColumnMapping } from '../csv-templates/csv-templates.mapper';
import type {
  CatiImportTemplateRequestDto,
  ChannelStatisticsResponseDto,
  CreateEmailSharingArgs,
  CreateEmailSharingRequestDto,
  CreateLinkSharingArgs,
  CreateLinkSharingRequestDto,
  CreateMessageSharingArgs,
  CreateMessageSharingRequestDto,
  GetMessageCampaignSmsProviderArgs,
  ExportCatiCallsArgs,
  ExportCatiCallsRequestDto,
  GetCatiImportTemplateArgs,
  GetChannelStatisticsArgs,
  GetChannelStatisticsRequestDto,
  GetIncomingEmailAddressArgs,
  ImportCatiCallsArgs,
  IncomingEmailAddressRequestDto,
  LinkChannelSettingsDto,
  MessageSharingErrorResponseDto,
  UpdateLinkChannelSettingsRequestDto,
} from './channel-publishing.api.types';
import type {
  ChannelStatistics,
  ChannelStatisticsType,
  LinkChannelSettings,
  LinkSharingSource,
  MessageSharingChannelType,
} from './types';

export function fromApiLinkChannelSettings({
  multiple_feedback_delay_period,
  prevent_multiple_feedback,
}: LinkChannelSettingsDto): LinkChannelSettings {
  return {
    multipleFeedbackDelayPeriod: multiple_feedback_delay_period,
    preventMultipleFeedback: prevent_multiple_feedback,
  };
}

export function toApiLinkChannelSettings({
  multipleFeedbackDelayPeriod,
  preventMultipleFeedback,
}: LinkChannelSettings): UpdateLinkChannelSettingsRequestDto {
  return {
    multiple_feedback_delay_period: multipleFeedbackDelayPeriod,
    prevent_multiple_feedback: preventMultipleFeedback,
  };
}

// A widget's statistics are found through its channel, despite the widget path.
const channelStatisticsPaths = {
  [ChannelType.EMAIL]: ({ campaignId }) => `/v1/email_campaigns/${campaignId}/stats`,
  [ChannelType.LINK]: ({ campaignId }) => `/v1/link_campaigns/${campaignId}/stats`,
  [ChannelType.PUSH_NOTIFICATION]: ({ campaignId }) => `/v1/push_notification_campaigns/${campaignId}/stats`,
  [ChannelType.SMS]: ({ campaignId }) => `/v1/sms_campaigns/${campaignId}/stats`,
  [ChannelType.WEB_WIDGET]: ({ channelId }) => `/v1/web_widgets/${channelId}/stats`,
  [ChannelType.WHATSAPP]: ({ campaignId }) => `/v1/whatsapp_campaigns/${campaignId}/stats`,
} satisfies Record<ChannelStatisticsType, (args: GetChannelStatisticsArgs) => string>;

export function toApiChannelStatisticsUrl(args: GetChannelStatisticsArgs): string {
  return channelStatisticsPaths[args.channelType](args);
}

export function toApiChannelStatistics({ fromDate, toDate }: GetChannelStatisticsArgs): GetChannelStatisticsRequestDto {
  return { from_date: dayjs(fromDate).format('YYYY-MM-DD'), to_date: dayjs(toDate).format('YYYY-MM-DD') };
}

// Legacy parity (the campaign logs serializers): unsent messages are those the contact policy or an unsubscription
// cancelled.
export function fromApiChannelStatistics(response: ChannelStatisticsResponseDto): ChannelStatistics {
  return {
    clicked: response.clicked ?? 0,
    created: response.created ?? 0,
    feedbackSubmitted: response.feedback_submitted ?? 0,
    opened: response.opened ?? 0,
    sent: response.sent ?? 0,
    unsent: (response.restricted_from_contact_policy ?? 0) + (response.unsubscription_caused_cancellation ?? 0),
  };
}

// The customer sources Link and Email sharing have in common.
function toApiSharingSource(
  source: LinkSharingSource,
): Pick<CreateLinkSharingRequestDto, 'column_mapping' | 'csv_file' | 'customer_list_id'> {
  if (source.type === 'customerList') {
    return { customer_list_id: source.customerListId };
  }

  return {
    column_mapping: toApiCsvColumnMapping(source.mapping),
    csv_file: { csv_string: source.csv, name: source.fileName },
  };
}

export function toApiCreateLinkSharing({
  includeShortLinks,
  source,
}: CreateLinkSharingArgs): CreateLinkSharingRequestDto {
  return { ...toApiSharingSource(source), short_link_include: includeShortLinks };
}

export function toApiCreateEmailSharing({
  deliveryTime,
  source,
}: CreateEmailSharingArgs): CreateEmailSharingRequestDto {
  return {
    ...(source.type === 'emails' ? { emails: source.emails } : toApiSharingSource(source)),
    ...(deliveryTime && { delivery_time: deliveryTime.toISOString() }),
  };
}

const messageSharingPaths = {
  [ChannelType.PUSH_NOTIFICATION]: 'push_notification',
  [ChannelType.SMS]: 'sms',
  [ChannelType.WHATSAPP]: 'whatsapp',
} satisfies Record<MessageSharingChannelType, string>;

export function toApiMessageSharingUrl({ campaignId, channelType }: CreateMessageSharingArgs): string {
  const path = messageSharingPaths[channelType];

  return `/v1/${path}_campaigns/${campaignId}/${path}_sharings`;
}

export function toApiMessageCampaignSmsProviderUrl({
  campaignId,
  channelType,
}: GetMessageCampaignSmsProviderArgs): string {
  return `/v1/${messageSharingPaths[channelType]}_campaigns/${campaignId}/sms_provider`;
}

// The API rejects a sharing whose campaign and account both lack an SMS provider.
export function isMissingSmsProviderError(error: unknown): boolean {
  const body = (error as { data?: MessageSharingErrorResponseDto } | undefined)?.data;

  return Boolean(body?.errors?.details?.sms_provider);
}

export function toApiCreateMessageSharing({ source }: CreateMessageSharingArgs): CreateMessageSharingRequestDto {
  return source.type === 'phoneNumbers' ? { phone_numbers: source.phoneNumbers } : toApiSharingSource(source);
}

export function toApiIncomingEmailAddress({
  channelId,
  flowId,
}: GetIncomingEmailAddressArgs): IncomingEmailAddressRequestDto {
  return { flow_id: flowId, node_id: channelId };
}

export function toApiExportCatiCalls({ channelId }: ExportCatiCallsArgs): ExportCatiCallsRequestDto {
  return { node_id: channelId };
}

export function toApiCatiImportTemplate({
  channelId,
  flowId,
}: GetCatiImportTemplateArgs): CatiImportTemplateRequestDto {
  return { flow_id: flowId, node_id: channelId };
}

// The API reads the file as multipart form data.
export function toApiImportCatiCalls({ channelId, file }: ImportCatiCallsArgs): FormData {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('node_id', channelId);

  return formData;
}
